/**
 * טופס מוצר — יצירה ועריכה (TASKS 3.3, PRD 7.4–7.5).
 *
 * ⚠️ מסך אחד לשני המצבים (`/admin/products/new` ו-`/admin/products/:id/edit`) —
 *    ⛔ אין `id` ב-URL ⇒ יצירה. הואריאנטים מנוהלים **רק** במצב עריכה: אין מק"ט
 *    לבנות עליו ואריאנט לפני שהמוצר עצמו נשמר ומקבל מק"ט מהשרת.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash } from '@phosphor-icons/react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { FormCard } from './FormCard.js';
import { isAdminApiError } from './adminApi.js';
import { type Category, listCategories } from './categoriesApi.js';
import { MediaManager } from './MediaManager.js';
import {
  createProduct,
  createVariant,
  deleteVariant,
  getProduct,
  PRICING_TYPES,
  updateProduct,
  updateVariant,
  type PricingType,
  type Variant,
} from './productsApi.js';

const PRICING_TYPE_LABEL: Record<PricingType, string> = {
  unit: 'יחידה - הלקוח בוחר כמות בלבד',
  per_meter: 'לפי מטר - הלקוח מזין אורך וכמות',
};

export function ProductFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const productId = id ? Number(id) : null;
  const isEdit = productId !== null;

  const [categories, setCategories] = useState<Category[]>([]);
  const [sku, setSku] = useState<string | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [specHtml, setSpecHtml] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [pricingType, setPricingType] = useState<PricingType>('unit');
  const [basePrice, setBasePrice] = useState('');
  const [unitLabel, setUnitLabel] = useState('');
  const [minOrderQty, setMinOrderQty] = useState('');
  const [qtyStep, setQtyStep] = useState('');
  const [priceIsEstimate, setPriceIsEstimate] = useState(false);
  const [priceOnRequest, setPriceOnRequest] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [variantSuffix, setVariantSuffix] = useState('');
  const [variantLabel, setVariantLabel] = useState('');
  const [variantPrice, setVariantPrice] = useState('');
  const [variantPriceOnRequest, setVariantPriceOnRequest] = useState(false);
  const [variantError, setVariantError] = useState<string | null>(null);
  // ⭐ נוסף 21.08.2026 — פידבק: טופס הוספת ואריאנט היה גלוי תמיד וצפוף
  //    (grid חמש עמודות), נראה לא מיושר ברוחב צר. עכשיו מוסתר מאחורי כפתור
  //    ונפתח כטופס מסודר, ⛔ לא משנה את השדות עצמם.
  const [showAddVariant, setShowAddVariant] = useState(false);
  // ⭐ נוסף 21.08.2026 — עקביות עם מחיקת קטגוריה/מדיה: מחיקת ואריאנט לא
  //    ביקשה אישור, בניגוד לכל שאר פעולות המחיקה בפאנל.
  const [confirmDeleteVariantId, setConfirmDeleteVariantId] = useState<number | null>(null);

  useEffect(() => {
    listCategories()
      .then(setCategories)
      .catch(() => {
        // ⭐ כשל טעינה ⛔ אינו חוסם — הבורר פשוט יישאר ריק וטל יראה שגיאת שמירה ברורה יותר
      });
  }, []);

  useEffect(() => {
    if (productId === null) return;
    let cancelled = false;

    getProduct(productId)
      .then(({ product, variants: productVariants }) => {
        if (cancelled) return;
        setSku(product.sku);
        setName(product.name);
        setDescription(product.description ?? '');
        setSpecHtml(product.spec_html ?? '');
        setCategoryId(product.category_id);
        setPricingType(product.pricing_type);
        setBasePrice(product.base_price);
        setUnitLabel(product.unit_label ?? '');
        setMinOrderQty(product.min_order_qty ?? '');
        setQtyStep(product.qty_step ?? '');
        setPriceIsEstimate(Boolean(product.price_is_estimate));
        setPriceOnRequest(Boolean(product.price_on_request));
        setIsActive(Boolean(product.is_active));
        setVariants(productVariants);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת המוצר.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (categoryId === '') {
      setError('יש לבחור קטגוריה.');
      return;
    }

    const input = {
      categoryId,
      name: name.trim(),
      description: description.trim() || undefined,
      specHtml: specHtml.trim() || undefined,
      pricingType,
      basePrice: Number(basePrice),
      unitLabel: pricingType === 'unit' && unitLabel.trim() ? unitLabel.trim() : undefined,
      minOrderQty: pricingType === 'per_meter' && minOrderQty ? Number(minOrderQty) : undefined,
      qtyStep: pricingType === 'per_meter' && qtyStep ? Number(qtyStep) : undefined,
      priceIsEstimate,
      priceOnRequest,
    };

    setSubmitting(true);
    try {
      if (isEdit && productId !== null) {
        await updateProduct(productId, { ...input, isActive });
        navigate('/admin/products');
      } else {
        const created = await createProduct(input);
        navigate(`/admin/products/${created.id}/edit`, { replace: true });
      }
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בשמירת המוצר.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddVariant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (productId === null) return;
    setVariantError(null);
    try {
      await createVariant(productId, {
        suffix: variantSuffix.trim(),
        label: variantLabel.trim(),
        price: variantPrice ? Number(variantPrice) : undefined,
        priceOnRequest: variantPriceOnRequest,
      });
      setVariantSuffix('');
      setVariantLabel('');
      setVariantPrice('');
      setVariantPriceOnRequest(false);
      setShowAddVariant(false);
      const { variants: refreshed } = await getProduct(productId);
      setVariants(refreshed);
    } catch (err) {
      setVariantError(isAdminApiError(err) ? err.message : 'שגיאה בהוספת ואריאנט.');
    }
  }

  async function handleToggleVariantActive(variant: Variant) {
    if (productId === null) return;
    try {
      await updateVariant(productId, variant.id, { isActive: !variant.is_active });
      const { variants: refreshed } = await getProduct(productId);
      setVariants(refreshed);
    } catch (err) {
      setVariantError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון ואריאנט.');
    }
  }

  async function handleToggleVariantPriceOnRequest(variant: Variant) {
    if (productId === null) return;
    try {
      await updateVariant(productId, variant.id, { priceOnRequest: !variant.price_on_request });
      const { variants: refreshed } = await getProduct(productId);
      setVariants(refreshed);
    } catch (err) {
      setVariantError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון ואריאנט.');
    }
  }

  // ⭐ נוסף 20.08.2026 — פער אמיתי שאריאנה זיהתה: היה אפשר להגדיר מחיר לוואריאנט
  //    רק ברגע היצירה (`handleAddVariant`); אחרי שהוואריאנט קיים, לא הייתה שום
  //    דרך בפאנל לעדכן את המחיר שלו מלבד מחיקה ויצירה מחדש (מאבד תמונות/סדר).
  //    כל וריאנט (מידה/צבע/כל שילוב) מקבל עכשיו שדה מחיר ניתן-לעריכה נפרד משלו.
  async function handleUpdateVariantPrice(variant: Variant, priceInput: string) {
    if (productId === null) return;
    const trimmed = priceInput.trim();
    const price = trimmed === '' ? null : Number(trimmed);
    if (price !== null && (Number.isNaN(price) || price < 0)) {
      setVariantError('מחיר הוואריאנט חייב להיות מספר חיובי.');
      return;
    }
    setVariantError(null);
    try {
      await updateVariant(productId, variant.id, { price });
      const { variants: refreshed } = await getProduct(productId);
      setVariants(refreshed);
    } catch (err) {
      setVariantError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון מחיר הוואריאנט.');
    }
  }

  async function handleDeleteVariant(variantId: number) {
    if (productId === null) return;
    try {
      await deleteVariant(productId, variantId);
      const { variants: refreshed } = await getProduct(productId);
      setVariants(refreshed);
    } catch (err) {
      setVariantError(isAdminApiError(err) ? err.message : 'שגיאה במחיקת ואריאנט.');
    }
  }

  if (loading) {
    return (
      <>
        <AdminNav />
        <p className="text-muted p-8">טוען…</p>
      </>
    );
  }

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-2xl px-6 py-12">
        <AdminPageHeader title={isEdit ? 'עריכת מוצר' : 'מוצר חדש'} />

        {/* ⭐ DESIGN.md 8.4 — "כרטיס לבן אחד לכל קבוצת שדות". */}
        <form className="mt-6 space-y-6" onSubmit={(e) => void handleSubmit(e)}>
          <FormCard title="פרטים כלליים">
            <div>
              <span className="block text-sm font-semibold">מק"ט</span>
              <p className="ltr-inline text-muted mt-1">
                {sku ?? 'יוקצה אוטומטית עם השמירה, לפי הקטגוריה שנבחר'}
              </p>
            </div>

            <div>
              <label htmlFor="p-name" className="block text-sm font-semibold">
                שם
              </label>
              <input
                id="p-name"
                required
                aria-required="true"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="p-category" className="block text-sm font-semibold">
                קטגוריה
              </label>
              <select
                id="p-category"
                required
                aria-required="true"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              >
                <option value="">- בחרו קטגוריה -</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="p-description" className="block text-sm font-semibold">
                תיאור
              </label>
              <textarea
                id="p-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="p-spec" className="block text-sm font-semibold">
                מפרט (HTML מוגבל)
              </label>
              <textarea
                id="p-spec"
                rows={4}
                value={specHtml}
                onChange={(e) => setSpecHtml(e.target.value)}
                aria-describedby="p-spec-hint"
                className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2 font-mono text-sm"
              />
              <p id="p-spec-hint" className="text-muted mt-1 text-xs">
                תגיות מותרות בלבד (p, ul, table וכו') - כל תגית וקישור אחר יוסרו אוטומטית בשמירה.
              </p>
            </div>
          </FormCard>

          <FormCard title="תמחור">
            <fieldset>
              <legend className="text-sm font-semibold">סוג תמחור</legend>
              <div className="mt-2 space-y-2">
                {PRICING_TYPES.map((type) => (
                  <label key={type} className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="pricingType"
                      value={type}
                      checked={pricingType === type}
                      onChange={() => setPricingType(type)}
                      className="mt-1"
                    />
                    {PRICING_TYPE_LABEL[type]}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="p-price" className="block text-sm font-semibold">
                מחיר בסיס (₪, לפני מע"מ)
              </label>
              <input
                id="p-price"
                type="number"
                min="0"
                step="0.01"
                required
                aria-required="true"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                className="tabular ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            {pricingType === 'unit' && (
              <div>
                <label htmlFor="p-unit-label" className="block text-sm font-semibold">
                  תווית יחידה (אופציונלי - למשל &quot;חבילה 50 מ&apos;&quot;)
                </label>
                <input
                  id="p-unit-label"
                  value={unitLabel}
                  onChange={(e) => setUnitLabel(e.target.value)}
                  className="mt-1 w-full rounded-md border border-line px-3 py-2"
                />
              </div>
            )}

            {pricingType === 'per_meter' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="p-min-qty" className="block text-sm font-semibold">
                    מינימום הזמנה (מטרים)
                  </label>
                  <input
                    id="p-min-qty"
                    type="number"
                    min="0"
                    step="0.01"
                    value={minOrderQty}
                    onChange={(e) => setMinOrderQty(e.target.value)}
                    className="tabular mt-1 w-full rounded-md border border-line px-3 py-2"
                  />
                </div>
                <div>
                  <label htmlFor="p-qty-step" className="block text-sm font-semibold">
                    קפיצת כמות (מטרים)
                  </label>
                  <input
                    id="p-qty-step"
                    type="number"
                    min="0"
                    step="0.01"
                    value={qtyStep}
                    onChange={(e) => setQtyStep(e.target.value)}
                    className="tabular mt-1 w-full rounded-md border border-line px-3 py-2"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={priceIsEstimate}
                  onChange={(e) => setPriceIsEstimate(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  מוצר &quot;מיוצר לפי מידה&quot; - המחיר ב-PDF יסומן בכוכבית כהערכה ראשונית, לא
                  כמחיר סופי (למשל: קופסת ניפוח, מתאם גמיש)
                </span>
              </label>
            </div>

            <div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={priceOnRequest}
                  onChange={(e) => setPriceOnRequest(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  מחיר לפי בקשה (הזמנה אישית - ללא מחיר קבוע) - המוצר יוצג ללא מחיר, וכל הצעה שתכלול
                  אותו תסומן כהצעה חלקית
                </span>
              </label>
            </div>
          </FormCard>

          {isEdit && (
            <FormCard title="סטטוס">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                מוצר פעיל (מוצג באתר)
              </label>
            </FormCard>
          )}

          {/* ⭐ DESIGN.md 8.4 — "כפתור שמירה דביק בתחתית הטופס בטפסים ארוכים". */}
          <div className="bg-canvas border-line sticky bottom-0 -mx-6 flex items-center gap-4 border-t px-6 py-4">
            {error && (
              <p role="alert" className="text-danger text-sm">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-cta" disabled={submitting}>
              {submitting ? 'שומר…' : 'שמירה'}
            </button>
          </div>
        </form>

        {isEdit && productId !== null && (
          <MediaManager productId={productId} title="תמונות המוצר" />
        )}

        {isEdit && productId !== null && (
          <section
            className="border-line bg-surface mt-10 rounded-lg border p-6"
            aria-labelledby="variants-h"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="variants-h" className="text-lg font-semibold">
                ואריאנטים
              </h2>
              {!showAddVariant && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddVariant(true)}
                >
                  <Plus size={16} weight="bold" aria-hidden="true" />
                  הוספת ואריאנט
                </button>
              )}
            </div>

            {variants.length === 0 && !showAddVariant && (
              <p className="text-muted mt-2 text-sm">אין עדיין ואריאנטים.</p>
            )}

            {/* ⭐ עודכן 21.08.2026 — כל ואריאנט הוא כרטיס לבן מוגבה (כמו FormCard)
                על רקע האפור של הסקשן, אותו דפוס "כרטיס על רצועה צבועה" שכבר
                קיים ב-HomePage/GalleryPage. המדיה של הוואריאנט מקבלת רצועה
                אפורה משלה בתוך הכרטיס - כדי שברור איפה מתחיל ומסתיים כל חלק. */}
            {variants.length > 0 && (
              <ul className="mt-4 space-y-4">
                {variants.map((variant) => (
                  <li
                    key={variant.id}
                    className="bg-canvas rounded-lg border border-line p-4 text-sm"
                    style={{ boxShadow: 'var(--shadow-card)' }}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span>
                        <span className="ltr-inline font-semibold">{variant.sku}</span> -{' '}
                        {variant.label}
                        {variant.price_on_request ? (
                          <span className="text-muted"> · מחיר לפי בקשה</span>
                        ) : (
                          variant.price && <span className="tabular"> · ₪{variant.price}</span>
                        )}
                      </span>
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => void handleToggleVariantPriceOnRequest(variant)}
                        >
                          {variant.price_on_request ? 'בטל מחיר-לפי-בקשה' : 'סמן מחיר-לפי-בקשה'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => void handleToggleVariantActive(variant)}
                        >
                          {variant.is_active ? 'פעיל' : 'מוסתר'}
                        </button>

                        {confirmDeleteVariantId === variant.id ? (
                          <>
                            <button
                              type="button"
                              className="text-danger rounded border border-line px-2 py-1"
                              onClick={() => void handleDeleteVariant(variant.id)}
                            >
                              לאשר מחיקה
                            </button>
                            <button
                              type="button"
                              className="rounded border border-line px-2 py-1"
                              onClick={() => setConfirmDeleteVariantId(null)}
                            >
                              ביטול
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            aria-label={`מחיקת ואריאנט ${variant.label}`}
                            className="flex h-11 w-11 items-center justify-center rounded border border-line"
                            onClick={() => setConfirmDeleteVariantId(variant.id)}
                          >
                            <Trash size={16} weight="regular" aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    </div>

                    <form
                      className="border-line mt-3 flex flex-wrap items-end gap-2 border-t pt-3"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const input = e.currentTarget.elements.namedItem(
                          `v-price-${variant.id}`,
                        ) as HTMLInputElement;
                        void handleUpdateVariantPrice(variant, input.value);
                      }}
                    >
                      <div>
                        <label
                          htmlFor={`v-price-${variant.id}`}
                          className="block text-xs font-semibold"
                        >
                          מחיר הוואריאנט (₪, לפני מע"מ)
                        </label>
                        <input
                          id={`v-price-${variant.id}`}
                          name={`v-price-${variant.id}`}
                          type="number"
                          step="0.01"
                          min="0"
                          defaultValue={variant.price ?? ''}
                          disabled={Boolean(variant.price_on_request)}
                          className="ltr-inline mt-1 w-32 rounded-md border border-line px-2 py-1 text-sm"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={Boolean(variant.price_on_request)}
                        className="rounded border border-line px-2 py-1 text-sm"
                      >
                        עדכון מחיר
                      </button>
                    </form>

                    {productId !== null && (
                      <div className="bg-surface border-line -mx-4 -mb-4 mt-3 rounded-b-lg border-t p-4">
                        <MediaManager
                          productId={productId}
                          variantId={variant.id}
                          title={`תמונות - ${variant.label}`}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {showAddVariant && (
              <div
                className="bg-canvas mt-4 rounded-lg border border-line p-5"
                style={{ boxShadow: 'var(--shadow-card)' }}
              >
                <h3 className="font-medium">ואריאנט חדש</h3>
                <form className="mt-4 space-y-4" onSubmit={(e) => void handleAddVariant(e)}>
                  <div>
                    <label htmlFor="v-suffix" className="block text-sm font-semibold">
                      סיומת (2-3 ספרות)
                    </label>
                    <input
                      id="v-suffix"
                      required
                      aria-required="true"
                      pattern="\d{2,3}"
                      value={variantSuffix}
                      onChange={(e) => setVariantSuffix(e.target.value)}
                      className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
                    />
                  </div>
                  <div>
                    <label htmlFor="v-label" className="block text-sm font-semibold">
                      תווית (למשל &quot;6 צול&quot; או &quot;שחור&quot;)
                    </label>
                    <input
                      id="v-label"
                      required
                      aria-required="true"
                      value={variantLabel}
                      onChange={(e) => setVariantLabel(e.target.value)}
                      className="mt-1 w-full rounded-md border border-line px-3 py-2"
                    />
                  </div>
                  <div>
                    <label htmlFor="v-price" className="block text-sm font-semibold">
                      מחיר (אופציונלי, ₪ לפני מע&quot;מ)
                    </label>
                    <input
                      id="v-price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={variantPrice}
                      onChange={(e) => setVariantPrice(e.target.value)}
                      className="tabular ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
                    />
                  </div>
                  <label
                    htmlFor="v-price-on-request"
                    className="flex items-center gap-2 rounded-md border border-line px-3 py-2 text-sm"
                  >
                    <input
                      id="v-price-on-request"
                      type="checkbox"
                      checked={variantPriceOnRequest}
                      onChange={(e) => setVariantPriceOnRequest(e.target.checked)}
                    />
                    מחיר לפי בקשה
                  </label>

                  <div className="flex items-center gap-3">
                    <button type="submit" className="btn btn-cta">
                      הוספה
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddVariant(false);
                        setVariantSuffix('');
                        setVariantLabel('');
                        setVariantPrice('');
                        setVariantPriceOnRequest(false);
                        setVariantError(null);
                      }}
                    >
                      ביטול
                    </button>
                  </div>
                </form>
              </div>
            )}

            {variantError && (
              <p role="alert" className="text-danger mt-3 text-sm">
                {variantError}
              </p>
            )}
          </section>
        )}
      </main>
    </>
  );
}
